import { useState, useEffect } from "react"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { format } from "date-fns"
import * as XLSX from "xlsx"
import {
  CalendarIcon,
  MapPin,
  Tag,
  Users,
  MessageSquare,
  X,
  Search,
  Eye,
  ArrowLeft,
  Loader2,
  Printer,
  Download,
  Upload,
} from "lucide-react"
import { formatDateHKT, formatSessionDateTimeHKT, formatDateTimeHKT } from "@/utils/dateTimeHKT"
import { ZubinEvent } from "@/types/event-types"
import RegistrationFormDialog from "@/components/events-builder/registration-form-dialog"
import WhatsAppMessageDialog from "@/components/events-builder/whatsapp-message-dialog"
import { BulkRegistrationDialog } from "@/components/events-builder/bulk-registration-dialog"
import { RegistrationForm } from "@/types/form-types"
import registrationService, { EventRegistration } from "@/services/registrationService"
import { formService } from "@/services/formService"
import eventService from "@/services/eventService"
import { useToast } from "@/hooks/use-toast"
import { useAuth } from "@/contexts/auth-context"

export default function ManageRegistrations() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const stateEvent = location.state?.event as ZubinEvent | undefined;

  const [event, setEvent] = useState<ZubinEvent | null>(stateEvent ?? null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "registered" | "cancelled" | "rejected">("registered");
  const [selectedRegistration, setSelectedRegistration] = useState<EventRegistration | null>(null);
  const [showWhatsAppDialog, setShowWhatsAppDialog] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  
  const [registrations, setRegistrations] = useState<EventRegistration[]>([]);
  const [registrationForm, setRegistrationForm] = useState<RegistrationForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch event by URL id so we always use the correct event (avoids wrong event/message when sending WhatsApp)
  useEffect(() => {
    if (!id) return;
    const fetchEvent = async () => {
      try {
        const eventData = await eventService.getEvent(id);

        // Check if staff user is authorized to manage registrations for this event
        const creatorObj =
          typeof eventData.createdBy === "object" && eventData.createdBy !== null
            ? (eventData.createdBy as { _id?: string; id?: string })
            : null;
        const createdById = creatorObj?._id || creatorObj?.id || (typeof eventData.createdBy === "string" ? eventData.createdBy : undefined);

        if (user?.role === "staff" && createdById && createdById !== user._id) {
          toast({
            title: "Access Denied",
            description: "Staff members can only manage registrations for events they have created.",
            variant: "destructive",
          });
          navigate("/manage/events-builder");
          return;
        }

        setEvent(eventData as unknown as ZubinEvent);
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load event';
        console.error('Error fetching event:', err);
        setError(errorMessage);
        setEvent(null);
        toast({ title: "Error", description: "Failed to load event.", variant: "destructive" });
      }
    };
    fetchEvent();
  }, [id, user, navigate, toast]);

  const fetchRegistrations = async () => {
    if (!event?._id) return;
    try {
      const registrationsData = await registrationService.getEventRegistrations(event._id);
      setRegistrations(registrationsData);
    } catch (err: unknown) {
      console.error('Error refreshing registrations:', err);
    }
  };

  // Fetch registrations and form data when event is available
  useEffect(() => {
    if (!event?._id) return;
    
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const registrationsData = await registrationService.getEventRegistrations(event._id);
        setRegistrations(registrationsData);
        
        if (event.registrationFormId) {
          const formData = await formService.getForm(event.registrationFormId);
          setRegistrationForm(formData);
        }
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load registrations';
        console.error('Error fetching data:', err);
        setError(errorMessage);
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [event?._id, event?.registrationFormId, toast]);

  // Filter registrations based on search query and status
  const filteredRegistrations = registrations.filter((registration) => {
    const searchString = `${registration.attendee.firstName} ${registration.attendee.lastName} ${registration.attendee.phone} ${registration.attendee.email || ""}`.toLowerCase();
    const matchesSearch = searchString.includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || registration.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleViewForm = (registration: EventRegistration) => {
    setSelectedRegistration(registration);
  };

  // Check if user can send WhatsApp messages (admin, staff, or event creator)
  const eventCreatorId =
    typeof event?.createdBy === "object" && event.createdBy !== null
      ? (event.createdBy as { _id?: string; id?: string })._id || (event.createdBy as { _id?: string; id?: string }).id
      : event?.createdBy;
  const canSendWhatsApp =
    user && (user.role === "admin" || user.role === "staff" || (eventCreatorId && eventCreatorId === user._id));

  // Get registered participants count
  const registeredParticipantsCount = registrations.filter(reg => reg.status === 'registered').length;

  const handleSendWhatsAppMessage = async (message: string, session?: string) => {
    if (!event?._id) {
      throw new Error("Event ID not found");
    }
    return await eventService.sendWhatsAppMessage(event._id, event.title ?? "", message, true, session);
  };

  const handleRejectRegistration = async (registrationId: string) => {
    try {
      await registrationService.updateRegistrationStatus(registrationId, 'rejected');
      
      // Update local state
      setRegistrations(prev => prev.map(reg =>
        reg._id === registrationId
          ? { ...reg, status: 'rejected' as const }
          : reg
      ));
      
      toast({
        title: "Success",
        description: "Registration rejected successfully",
      });
      
      setSelectedRegistration(null);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Failed to reject registration";
      console.error('Error rejecting registration:', err);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  // Get session details for a registration
  const getSessionDetails = (sessionIds: string[]) => {
    if (!event?.sessions) return [];
    return sessionIds
      .map(sessionId => event.sessions.find(session => session._id === sessionId))
      .filter(session => session)
      .map(session => ({
        title: session!.title,
        date: new Date(session!.date),
        startTime: session!.startTime,
        endTime: session!.endTime
      }));
  };

  // Print functionality
  const handlePrint = () => {
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Event Registrations - ${event?.title}</title>
          <style>
            @media print {
              @page {
                size: A4;
                margin: 1cm;
              }
            }
            body {
              font-family: Arial, sans-serif;
              line-height: 1.6;
              margin: 0;
              padding: 20px;
              font-size: 12px;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 2px solid #333;
              padding-bottom: 20px;
            }
            .event-details {
              margin-bottom: 30px;
              padding: 15px;
              background-color: #f9f9f9;
              border-radius: 5px;
            }
            .event-details h2 {
              margin: 0 0 15px 0;
              color: #333;
            }
            .event-info {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 10px;
              margin-bottom: 15px;
            }
            .event-info div {
              display: flex;
              align-items: center;
            }
            .event-info strong {
              min-width: 120px;
              display: inline-block;
            }
            .registrations-table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            .registrations-table th,
            .registrations-table td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
              word-wrap: break-word;
              max-width: 200px;
            }
            .registrations-table th {
              background-color: #f2f2f2;
              font-weight: bold;
            }
            .registrations-table tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .sessions-list {
              max-width: 250px;
              word-wrap: break-word;
              padding: 8px;
            }
            .status-badge {
              padding: 2px 6px;
              border-radius: 3px;
              font-size: 10px;
              font-weight: bold;
            }
            .status-registered {
              background-color: #d4edda;
              color: #155724;
            }
            .status-cancelled {
              background-color: #f8d7da;
              color: #721c24;
            }
            .status-rejected {
              background-color: #f8d7da;
              color: #721c24;
            }
            .footer {
              margin-top: 30px;
              text-align: center;
              font-size: 10px;
              color: #666;
            }
            @media print {
              .no-print {
                display: none;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Event Registrations Report</h1>
            <p>Generated on ${formatDateTimeHKT(new Date())}</p>
          </div>
          
          <div class="event-details">
            <h2>${event?.title}</h2>
            <div class="event-info">
              <div><strong>Date:</strong> ${event?.startDate ? formatDateHKT(new Date(event.startDate)) : 'N/A'}</div>
              <div><strong>Location:</strong> ${event?.location?.venue || 'N/A'}</div>
              <div><strong>Category:</strong> ${event?.category || 'N/A'}</div>
              <div><strong>Target Group:</strong> ${event?.targetGroup || 'N/A'}</div>
              <div><strong>Total Sessions:</strong> ${event?.sessions?.length || 0}</div>
              <div><strong>Total Registrations:</strong> ${filteredRegistrations.length}</div>
            </div>
          </div>
          
          <table class="registrations-table">
            <thead>
              <tr>
                <th>No.</th>
                <th>Participant Name</th>
                <th>Mobile Number</th>
                <th>Email</th>
                <th>Sessions Registered (Date & Time)</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${filteredRegistrations.map((registration, index) => {
                const sessionDetails = getSessionDetails(registration.sessions);
                const sessionsHtml = sessionDetails.length > 0 
                  ? sessionDetails.map(session => 
                      `<div style="margin-bottom: 8px; padding: 4px; border-left: 3px solid #3b82f6; background-color: #f8fafc;">
                        <div style="font-weight: bold; font-size: 11px; color: #1f2937;">${session.title}</div>
                        <div style="font-size: 10px; color: #6b7280;">${formatSessionDateTimeHKT(session.date, session.startTime, session.endTime)}</div>
                      </div>`
                    ).join('')
                  : 'No sessions selected';
                return `
                  <tr>
                    <td>${index + 1}</td>
                    <td>${registration.attendee.firstName} ${registration.attendee.lastName}</td>
                    <td>${registration.attendee.phone}</td>
                    <td>${registration.attendee.email || '-'}</td>
                    <td class="sessions-list">${sessionsHtml}</td>
                    <td><span class="status-badge status-${registration.status}">${registration.status.charAt(0).toUpperCase() + registration.status.slice(1)}</span></td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
          
          <div class="footer">
            <p>The Zubin Foundation Event Management System</p>
            <p>This report contains ${filteredRegistrations.length} registration(s)</p>
          </div>
        </body>
      </html>
    `;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    iframe.style.left = '-9999px';
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentWindow?.document;
    if (!iframeDoc || !iframe.contentWindow) {
      document.body.removeChild(iframe);
      return;
    }
    iframeDoc.open();
    iframeDoc.write(printContent);
    iframeDoc.close();

    setTimeout(() => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
    }, 500);
  };

  const handleExportExcel = () => {
    if (!event || !registrationForm) return;

    const wb = XLSX.utils.book_new();

    const sessionsSummary = event.sessions?.length
      ? event.sessions.map(s => s.title).join(", ")
      : "N/A";

    const dateRange = event.startDate
      ? formatDateHKT(new Date(event.startDate)) +
        (event.startDate !== event.endDate && event.endDate
          ? ` - ${formatDateHKT(new Date(event.endDate))}`
          : "")
      : "N/A";

    const timeRange = event.sessions?.length
      ? event.sessions.map(s => `${s.startTime} - ${s.endTime} HKT`).join(", ")
      : "N/A";

    const headerRows: (string | undefined)[][] = [
      ["The Zubin Foundation"],
      [],
      ["Event:", event.title],
      ["Session(s):", sessionsSummary],
      ["Date:", dateRange],
      ["Time:", timeRange],
      [],
    ];

    const formFields: { label: string; sectionId: string; fieldId: string }[] = [];
    for (const section of registrationForm.sections) {
      for (const field of section.fields) {
        formFields.push({ label: field.label, sectionId: section._id, fieldId: field._id });
      }
    }

    const tableHeaders = [
      "No.",
      "First Name",
      "Last Name",
      "Mobile Number",
      "Email",
      "Sessions Registered",
      ...formFields.map(f => f.label),
      "Status",
      "Registered At",
    ];
    headerRows.push(tableHeaders);

    const dataRows = filteredRegistrations.map((reg, idx) => {
      const sessionDetails = getSessionDetails(reg.sessions);
      const sessionsText = sessionDetails.length > 0
        ? sessionDetails.map(s => `${s.title} (${formatSessionDateTimeHKT(s.date, s.startTime, s.endTime)})`).join("; ")
        : "No sessions selected";

      const fieldValues = formFields.map(f => {
        const resp = reg.formResponses.find(
          r => r.sectionId === f.sectionId && r.fieldId === f.fieldId
        );
        if (!resp) return "";
        const val = resp.response;
        if (Array.isArray(val)) return val.join(", ");
        return val != null ? String(val) : "";
      });

      return [
        idx + 1,
        reg.attendee.firstName,
        reg.attendee.lastName,
        reg.attendee.phone,
        reg.attendee.email || "",
        sessionsText,
        ...fieldValues,
        reg.status.charAt(0).toUpperCase() + reg.status.slice(1),
        reg.registeredAt ? format(new Date(reg.registeredAt), "MMM d, yyyy") : "",
      ];
    });

    const allRows = [...headerRows, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(allRows);

    const totalCols = tableHeaders.length;
    ws["!cols"] = Array.from({ length: totalCols }, () => ({ wch: 20 }));
    if (totalCols > 0) {
      ws["!cols"][0] = { wch: 6 };
    }

    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: Math.min(totalCols - 1, 3) } },
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Registrations");

    const safeTitle = (event.title || "Event").replace(/[^a-zA-Z0-9_ -]/g, "").substring(0, 50);
    XLSX.writeFile(wb, `${safeTitle}_Registrations.xlsx`);
  };

  type RegistrationStatus = "registered" | "cancelled" | "rejected";

  const getStatusVariant = (status: RegistrationStatus) => {
    switch (status) {
      case "registered":
        return "default";
      case "cancelled":
        return "destructive";
      case "rejected":
        return "destructive";
      default:
        return "outline";
    }
  };

  const getStatusClassName = (status: RegistrationStatus) => {
    switch (status) {
      case "registered":
        return "bg-green-100 text-green-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (!event) {
    return (
      <div className="container mx-auto px-4 py-8">
        <p>Event not found</p>
        <Button onClick={() => navigate("/manage/events-builder")}>Back to Events</Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin mr-2" />
          <span>Loading registrations...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={() => navigate("/manage/events-builder")}>Back to Events</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 space-y-8">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate("/manage/events-builder")}
        className="mb-4"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Events Builder
      </Button>

      {/* Event Details Section */}
      <Card>
        <CardHeader>
          <CardTitle>Event Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <h2 className="text-2xl font-bold">{event.title}</h2>
                <div className="flex items-center mt-2 text-gray-500">
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  <span>
                    {formatDateHKT(new Date(event.startDate))}
                    {event.startDate !== event.endDate && ` - ${formatDateHKT(new Date(event.endDate))}`}
                  </span>
                </div>
              </div>
              <div className="flex items-center text-gray-500">
                <MapPin className="h-4 w-4 mr-2" />
                <span>
                  {event.location.venue}
                  {event.location.address && `, ${event.location.address}`}
                  {event.location.district && `, ${event.location.district}`}
                </span>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-center">
                <Tag className="h-4 w-4 mr-2 text-gray-500" />
                <Badge variant="outline" className="bg-gray-50">
                  {event.category}
                </Badge>
              </div>
              <div className="flex items-center">
                <Users className="h-4 w-4 mr-2 text-gray-500" />
                <Badge variant="outline" className="bg-gray-50">
                  {event.targetGroup}
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Registrations Section */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>
              Registrations ({filteredRegistrations.length})
            </CardTitle>
            <div className="flex gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
                <Input
                  placeholder="Search registrations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-[300px]"
                />
              </div>
              <Select value={statusFilter} onValueChange={(value: typeof statusFilter) => setStatusFilter(value)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="registered">Registered</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              <Button
                onClick={handleExportExcel}
                variant="outline"
                className="bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200"
                disabled={!registrationForm}
              >
                <Download className="h-4 w-4 mr-2" />
                Export Excel
              </Button>
              <Button
                onClick={() => setBulkUploadOpen(true)}
                variant="outline"
                className="bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border-indigo-200"
              >
                <Upload className="h-4 w-4 mr-2" />
                Bulk Upload
              </Button>
              <Button
                onClick={handlePrint}
                variant="outline"
                className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print Report
              </Button>
              {canSendWhatsApp && registeredParticipantsCount > 0 && (
                <Button
                  onClick={() => setShowWhatsAppDialog(true)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Send WhatsApp
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredRegistrations.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {registrations.length === 0 ? "No registrations found for this event." : "No registrations match your search criteria."}
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Participant</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Sessions Registered (with Date & Time)</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRegistrations.map((registration) => {
                    const sessionDetails = getSessionDetails(registration.sessions);
                    return (
                      <TableRow key={registration._id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">
                              {registration.attendee.firstName} {registration.attendee.lastName}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <p className="text-sm">{registration.attendee.phone}</p>
                            {registration.attendee.email && (
                              <p className="text-sm text-gray-500">{registration.attendee.email}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="max-w-xs">
                            {sessionDetails.length > 0 ? (
                              <div className="space-y-2">
                                {sessionDetails.map((session, index) => (
                                  <div key={index} className="border rounded p-2 bg-gray-50 mb-2">
                                    <div className="font-medium text-xs text-gray-900 mb-1">
                                      {session.title}
                                    </div>
                                    <div className="text-xs text-gray-600">
                                      {formatSessionDateTimeHKT(session.date, session.startTime, session.endTime)}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-500 text-sm">No sessions selected</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={getStatusVariant(registration.status)}
                            className={getStatusClassName(registration.status)}
                          >
                            {registration.status.charAt(0).toUpperCase() + registration.status.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewForm(registration)}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              View Form
                            </Button>
                            {registration.status !== "cancelled" && registration.status !== "rejected" && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                  >
                                    <X className="h-4 w-4 mr-2" />
                                    Reject
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Reject Registration</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Are you sure you want to reject the registration for{" "}
                                      <strong>{registration.attendee.firstName} {registration.attendee.lastName}</strong>?
                                      This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>
                                      Cancel
                                    </AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleRejectRegistration(registration._id)}
                                      className="bg-red-600 hover:bg-red-700"
                                    >
                                      Reject Registration
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Registration Form Dialog */}
      {selectedRegistration && registrationForm && (
        <RegistrationFormDialog
          isOpen={!!selectedRegistration}
          onClose={() => setSelectedRegistration(null)}
          registration={selectedRegistration}
          form={registrationForm}
          onReject={handleRejectRegistration}
        />
      )}

      {/* WhatsApp Message Dialog */}
      {showWhatsAppDialog && (
        <WhatsAppMessageDialog
          isOpen={showWhatsAppDialog}
          onClose={() => setShowWhatsAppDialog(false)}
          eventTitle={event.title}
          participantCount={registeredParticipantsCount}
          sessions={event.sessions}
          staffContact={event.staffContact}
          onSendMessage={handleSendWhatsAppMessage}
        />
      )}

      {/* Bulk Registration Dialog */}
      {event && (
        <BulkRegistrationDialog
          open={bulkUploadOpen}
          onOpenChange={setBulkUploadOpen}
          eventId={event._id}
          eventTitle={event.title}
          onSuccess={fetchRegistrations}
        />
      )}
    </div>
  );
} 